// Conversations Module Tour
// Unified inbox for all communication channels

export const conversationsTour = {
  id: 'conversations',
  name: 'Conversations Module Tour',
  displayName: 'Your Unified Communication Hub',
  description: 'Master all your messages in one powerful inbox',
  estimatedDuration: '4 minutes',
  category: 'feature',
  autoStart: false,
  prerequisiteTours: ['ftux'],
  
  config: {
    animate: true,
    overlayOpacity: 0.75,
    smoothScroll: true,
    allowClose: true,
    showProgress: true
  },
  
  steps: [
    // Step 1: Welcome to Conversations
    {
      element: 'nav #sb_conversations',
      popover: {
        title: '💬 Welcome to Conversations',
        description: `
          This is your unified inbox—every message from every channel in one place. 
          SMS, email, live chat, Facebook, Instagram, and Google Business Messages 
          all converge here. No more switching between apps!
        `,
        side: 'right',
        align: 'center'
      }
    },

    // Step 2: Conversation List Sidebar
    {
      element: '[class*="conversation-list"], [class*="inbox-list"]',
      popover: {
        title: '📥 Your Conversation List',
        description: `
          Every conversation with your contacts shows up here. You'll see the contact's 
          name, a preview of the last message, and which channel it came from (SMS, 
          email, etc.). Click any conversation to open it and respond.
        `,
        side: 'right',
        align: 'start'
      }
    },

    // Step 3: Filter Tabs
    {
      element: '[class*="filter"], button:contains("Unread"), button:contains("All")',
      popover: {
        title: '🎯 Filter Your Messages',
        description: `
          Use these filters to focus on what matters:
          • **Unread**: New messages waiting for your response
          • **Recents**: All recent conversations
          • **Starred**: Conversations you've marked as important
          • **All**: Everything, regardless of status
          
          Stay organized and never miss an important message.
        `,
        side: 'right',
        align: 'start',
        proTip: 'Star important conversations so you can find them instantly later!'
      }
    },

    // Step 4: Conversation Detail Panel
    {
      element: '#sb_conversations',
      popover: {
        title: '💭 Conversation Thread',
        description: `
          When you select a conversation, you'll see the full message history here. 
          All messages with this contact are displayed in chronological order—
          whether they came via SMS, email, Facebook, or any other channel. 
          It's your complete communication timeline.
        `,
        side: 'left',
        align: 'center'
      }
    },

    // Step 5: Message Composer
    {
      element: '[class*="composer"], [class*="message-input"], textarea',
      popover: {
        title: '✍️ Send Messages',
        description: `
          Type your message here and hit send. The system automatically uses the 
          same channel the contact last used. You can also switch channels using 
          the tabs above (SMS, Email, etc.) to send via a specific method.
        `,
        side: 'top',
        align: 'center'
      }
    },

    // Step 6: Channel Tabs (SMS, Email, etc.)
    {
      element: '[role="tab"], [class*="channel-tab"]',
      popover: {
        title: '📱 Switch Communication Channels',
        description: `
          These tabs let you choose how to communicate:
          • **SMS**: Send text messages
          • **Email**: Send emails
          • **Call**: Make phone calls (if configured)
          • **Social**: Facebook/Instagram messages
          
          Each channel has its own composer optimized for that type of message.
        `,
        side: 'top',
        align: 'center'
      }
    },

    // Step 7: Contact Info Sidebar
    {
      element: '[class*="contact-info"], [class*="sidebar"]',
      popover: {
        title: '👤 Contact Information',
        description: `
          While chatting, you can see the contact's full profile here:
          • Name, email, phone
          • Tags and custom fields
          • Recent activities
          • Notes and tasks
          
          Click on the contact's name to open their full contact card.
        `,
        side: 'left',
        align: 'start'
      }
    },

    // Step 8: Internal Notes
    {
      popover: {
        title: '📝 Internal Notes',
        description: `
          Add notes to any conversation that only your team can see. These notes 
          don't go to the contact—they're for internal collaboration. Perfect for 
          logging call summaries, follow-up reminders, or context for teammates.
          
          Look for the "Add Note" button in the conversation.
        `,
        side: 'over',
        align: 'center'
      }
    },

    // Step 9: Assign Conversations
    {
      popover: {
        title: '🎯 Assign to Team Members',
        description: `
          Working with a team? Assign conversations to specific people. The assigned 
          person gets notified and the conversation appears in their queue. Great for 
          distributing workload and ensuring accountability.
          
          Look for the assignment dropdown in the conversation toolbar.
        `,
        side: 'over',
        align: 'center',
        proTip: 'Set up rules in Automation to auto-assign conversations based on tags, keywords, or time of day!'
      }
    },

    // Step 10: Templates & Snippets
    {
      element: '#tb_conversations-templates',
      popover: {
        title: '⚡ Templates & Snippets',
        description: `
          Save time with pre-written message templates. Create snippets for common 
          responses like appointment confirmations, pricing info, or FAQs. Just type 
          a shortcut (like "/hours") and the full message appears instantly.
        `,
        side: 'left',
        align: 'center',
        proTip: 'Create templates with merge fields like {{contact.first_name}} for personalized mass messages!'
      }
    },

    // Step 11: Manual Actions
    {
      element: '#tb_manial-actions',
      popover: {
        title: '✋ Manual Actions',
        description: `
          When workflows require human approval, manual actions appear here. These are 
          automated sequences paused waiting for you to review and proceed. For example: 
          "Approve this proposal before sending" or "Review before scheduling."
        `,
        side: 'right',
        align: 'center'
      }
    },

    // Step 12: Search Conversations
    {
      element: 'input[type="search"], input[placeholder*="Search"]',
      popover: {
        title: '🔍 Search All Messages',
        description: `
          Find any conversation or message instantly. Search by contact name, message 
          content, date, or even attachment names. The search looks through your 
          entire message history across all channels.
        `,
        side: 'bottom',
        align: 'start'
      }
    },

    // Step 13: Completion
    {
      popover: {
        title: '🎉 You\'re a Communication Pro!',
        description: `
          Excellent! You now know how to:
          ✅ View all messages in one unified inbox
          ✅ Respond via SMS, email, or social media
          ✅ Filter and search conversations
          ✅ Add internal notes for your team
          ✅ Use templates to save time
          ✅ Assign conversations to teammates
          
          **Next Steps:**
          • Set up message templates for common responses
          • Configure notifications so you never miss messages
          • Explore the Calendar tour to book appointments via chat
          
          Never miss a message again! 💪
        `,
        side: 'over',
        align: 'center',
        showButtons: ['close'],
        doneBtnText: 'Got it! →'
      }
    }
  ]
};
